import $ from 'jquery';

/**
 * @class
 * @classdesc Universal carousel element javascripts. Carousels uses Slick library.
 *            Unfortunately Slick uses jQuery, so we have to use jQuery for initializing Slick.
 *
 * @author Eero Ahola <eero.ahola@crasman.fi>
 *
 * @property {object} carouselRootElement - Carousel root element is stored as property
 * @property {object} [carouselSettings]    - Custom carousel settings
 *
 * @example
 * import Carousel from 'path/to/carousel';
 *
 * // Example for creating multiple carousel on same page
 * const exampleCarousels     = document.querySelectorAll('.all-carousels'),
 *       initializedCarousels = new Carousel(exampleCarousel);
 *
 * // Example for creating single carousel with querySelected element
 * const exampleCarousel      = document.getElementById('example-carousel'),
 *        initializedCarousel = new Carousel(exampleCarousels);
 */
class Carousel
{
  /**
   * Constructor of carousel element
   *
   * @param {object|string} carouselRootElement - Carousel root element either already querySelected or querySelect string
   * @param {object}        carouselSettings    - Carousel settings object, if not using defaults.
   *
   * @constructor
   */
  constructor(carouselRootElement, carouselSettings) {
    this.carouselRootElement = carouselRootElement;
    this.carouselSettings = typeof carouselSettings === 'undefined' ? false : carouselSettings;

    this._initCarouselSettings();
  }

  isInitialized() {
    return !!this.carouselRootElement.slick;
  }

  /**
   * Initialize carousel settings
   *
   * @returns {boolean|undefined} - Returns false, if carouselRootElement could not found
   * @private
   */
  _initCarouselSettings() {

    const slickBreakpoints = [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2
        }
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1
        }
      }
    ];

    // Define default settings for carousel
    if(!this.carouselSettings) {
      this.carouselSettings = {
        dots: true,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: true,
        rows: 0//Removes inline styles from the carousel cards
      }
    }

    if(typeof this.carouselRootElement === 'string') {
      this.carouselRootElement = document.querySelectorAll(this.carouselRootElement);
    }

    if(!this.carouselRootElement) {
      console.warn('Could not find carousel element to initailize!');
      return false;
    }

    // If instance of carouselRootElement is HTMLElement (used querySelector, do not loop)
    if(this.carouselRootElement instanceof HTMLElement) {
      if(this.carouselRootElement.querySelectorAll(':scope > *').length > 1) {
        this.carouselSettings.slidesToShow = parseInt(this.carouselRootElement.dataset.slidestoshow) || 1;
        this.carouselSettings.slidesToScroll = parseInt(this.carouselRootElement.dataset.slidestoscroll) || 1;

        this.carouselSettings.prevArrow = this.carouselSettings.prevArrow || this.carouselRootElement.parentNode.querySelector('.carousel-nav.carousel-prev') || false;
        this.carouselSettings.nextArrow = this.carouselSettings.nextArrow || this.carouselRootElement.parentNode.querySelector('.carousel-nav.carousel-next') || false;

        if (this.carouselSettings.slidesToShow > 1) {
          this.carouselSettings.responsive = slickBreakpoints;
        }

        this._createCarousel(this.carouselRootElement);
      }
    } else {
      // Check if carousel has more than one item, then initialize whole thing
      // Otherwise keep it simple and no carousels to one image only.
      for(let i = 0; i < this.carouselRootElement.length; i++) {
        if(this.carouselRootElement[i].querySelectorAll(':scope > *').length > 1) {
          this.carouselSettings.slidesToShow = parseInt(this.carouselRootElement[i].dataset.slidestoshow) || 1;
          this.carouselSettings.slidesToScroll = parseInt(this.carouselRootElement[i].dataset.slidestoscroll) || 1;
          this.carouselSettings.prevArrow = this.carouselRootElement[i].parentNode.querySelector('.carousel-nav.carousel-prev') || false;
          this.carouselSettings.nextArrow = this.carouselRootElement[i].parentNode.querySelector('.carousel-nav.carousel-next') || false;

          if (this.carouselSettings.slidesToShow > 1) {
            this.carouselSettings.responsive = slickBreakpoints;
          } else {
            this.carouselSettings.responsive = [];
          }

          this._createCarousel(this.carouselRootElement[i]);
        }
      }
    }
  }

  /**
   * Creates a slick carousel using jQuery
   *
   * @param {object} carouselElement - Carousel element to create
   *
   * @returns {undefined}
   * @private
   */
  _createCarousel(carouselElement) {
    $(carouselElement).slick(this.carouselSettings);

    if( typeof this.carouselSettings.beforeChangeCallback === 'function' ) {
      $(carouselElement).on('beforeChange', this.carouselSettings.beforeChangeCallback);
    }

    if( typeof this.carouselSettings.afterChangeCallback === 'function' ) {
      $(carouselElement).on('afterChange', this.carouselSettings.afterChangeCallback);
    }
  }

}

export default Carousel;
